// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.30.0 — the two halves of the articles measurement.
 *
 * Canonical, and deliberately the ONLY implementation: the census scripts, the
 * source gate and the backend coverage service all import it from here. Two
 * copies of a parser is two places for the denominator to disagree, and this
 * series exists because a denominator was wrong.
 *
 * ## Why this is a library and not two regexes at two call sites
 *
 * The first measurement of this corpus, taken on 2026-09-23, reported that
 * `VN_AI_LAW_2026` cites **zero** articles across 17 requirements. It cites
 * plenty. The regex was `/(?:Art\.?\s*|Điều\s*)(\d+)/` and the references read
 * `"Law 134/2025/QH15 Articles 1–7"` — `Art` is followed by `icles`, not by a
 * digit, so every English plural and every range fell through. The conclusion
 * drawn from it ("this record has no traceability at all") was wrong about that
 * record and right about `VN_DATA_LAW`, and there was no way to tell which from
 * the output.
 *
 * So both halves of the join live here, are used by the check and by the
 * coverage reader, and are tested against fixtures that include every shape the
 * corpus actually contains. A measurement instrument that is wrong is worse
 * than no measurement: it produces a number people then reason from.
 *
 * ## The two halves
 *
 * {@link articlesInSource} is the DENOMINATOR — what the instrument contains,
 * read from the committed signed text. This is the half that cannot be
 * satisfied from inside the pack, and it is the whole point: every other gate
 * in this repository measures the pack against itself, which a partial pack
 * satisfies perfectly.
 *
 * {@link articlesInReference} is the NUMERATOR's input — which articles a
 * requirement's `reference` string names.
 */
/**
 * Every article number a committed signed text declares, in order.
 *
 * Matches a heading at the start of a line only. A cross-reference inside a
 * paragraph ("theo quy định tại Điều 12") is NOT a declaration of Điều 12 and
 * must not inflate the denominator — which is why this anchors to `^` and the
 * reference parser below does not.
 *
 * Both the Vietnamese `Điều` and the English `Article` forms are accepted, so
 * the same function serves an OCR'd Công báo text and a retrieved EU
 * instrument.
 *
 * @param text page-delimited source text
 * @returns ascending, de-duplicated
 */
export function articlesInSource(text) {
    if (typeof text !== 'string' || text.length === 0)
        return [];
    const found = new Set();
    // The leading class absorbs OCR noise, and absorbing it is not optional:
    // the committed Decree 356 scan renders two of its headings as `: Điều 7.`
    // and `_ Điều 34.`, so a bare `^[ \t]*` anchor silently read 39 of 41
    // articles. That would have under-reported the denominator by two and
    // reported a pack as complete over an instrument it does not fully carry —
    // the exact failure this series exists to end. Caught by the real-source
    // floor in `article-parser.test.ts`, not by reading.
    //
    // It stays anchored to the LINE START. That is what keeps a cross-reference
    // inside a paragraph ("theo quy định tại Điều 12") out of the denominator,
    // and it is the property the noise class must not cost.
    // **Sprint 62.31.0 widened this twice more, and both were found by GAPS in a
    // contiguous run rather than by reading.** Three committed cybersecurity
    // sources parsed with a hole in the middle — Decree 331 missing Điều 34,
    // Decree 341 missing Điều 11, Decree 328 missing Điều 14 — and a hole is the
    // only symptom this defect has. `framework-articles-match-source.test.ts`
    // compares `articles.json` to the source, so a heading the parser cannot see
    // is absent from BOTH sides and the two agree perfectly at the wrong number.
    //
    // What was actually there, derived across all 28 committed source texts
    // rather than guessed at:
    //
    //   * `Điêu 34.` and `Điêu 14.` — the OCR dropped the tone mark from `ề`.
    //     **3 occurrences against 723 clean ones**, which is why it survived: it
    //     is rare enough to look like an instrument that skips a number.
    //   * `, Điều 11.` — a leading comma. One occurrence.
    //
    // The leading class is now the observed debris set rather than a widened
    // guess. `l` and `Ỉ` are stray characters an OCR emits for a bullet;
    // including them is safe because the class must absorb EVERYTHING between the
    // line start and the word, so a real sentence like `là Điều 5 của Luật` stops
    // at the `à`.
    //
    // It stays anchored to the LINE START. That is what keeps a cross-reference
    // inside a paragraph ("theo quy định tại Điều 12") out of the denominator,
    // and it is the property none of this widening may cost.
    const re = headingPattern();
    let m;
    // Either limb may be the one that matched, so read whichever captured.
    while ((m = re.exec(text)) !== null)
        found.add(Number(m[1] ?? m[2]));
    return [...found].sort((a, b) => a - b);
}
/**
 * A fresh matcher each call. The pattern is `/g`, so it carries `lastIndex`
 * between uses and a shared instance would hand the second caller whatever
 * position the first one stopped at.
 */
function headingPattern() {
    // Two limbs, because the two drafting traditions put the article TITLE in
    // different places.
    //
    // A Vietnamese instrument writes `Điều 12. Quyền của chủ thể dữ liệu` — number,
    // punctuation and title on one line. The trailing `[.:—-]` is what keeps a
    // cross-reference out of the denominator once the noise class has absorbed
    // whatever sat before the word.
    //
    // **Sprint 62.34.0 — an EU instrument writes the number and the title on
    // SEPARATE lines**, with no punctuation after the number at all:
    //
    //     Article 12
    //     Transparent information, communication and modalities ...
    //
    // So the first limb reads ZERO articles from the GDPR, NIS2, DORA and the
    // ePrivacy Directive — four instruments this corpus carries — and 62.34.0's
    // AC3 is the criterion that found it. The second limb requires the number to
    // be the WHOLE line, which is a stricter test than the first rather than a
    // looser one: `Article 16(2) TFEU mandates …` and `Articles 8, 10 and 11
    // shall apply …` both begin a line in these texts and neither matches,
    // because something follows the number.
    //
    // The `$` alternative is deliberately NOT given to `Điều`. A Vietnamese
    // table of contents lists `Điều 5` on a line of its own, and granting it
    // would double every article in a source whose PDF carries one. Measured as
    // identical across all 55 mapped sources before and after, which is the
    // assertion `article-parser.test.ts` holds this to.
    return /^[ \t_:.|—,'"“”¬lỈ-]*(?:Đi[êề]u\s+(\d{1,3})\s*[.:—-]|Article\s+(\d{1,3})\s*(?:[.:—-]|$))/gmu;
}
/**
 * Sprint 62.34.2 — the articles an instrument declares as ITS OWN.
 *
 * ## The problem this exists for
 *
 * {@link articlesInSource} anchors on the line start and knows nothing about
 * which instrument it is reading. An AMENDING instrument quotes the amended
 * instrument's replacement articles in full, so its text carries TWO
 * instruments' headings. Decree 320/2026 amends Decree 69/2024: its own
 * articles run Điều 1 to `Điều 20. Điều khoản thi hành`, and in between it
 * quotes `“Điều 7.`, `“Điều 8.`, `“Điều 9.`, `“Điều 16.`, `“Điều 18.` and
 * `“Điều 35.` The flat reading returns 21 numbers with a highest of 35 for a
 * decree that has twenty articles — an under-count of one instrument and an
 * over-count of the other. `framework-articles-match-source.test.ts` cannot
 * see it, because a map built from the same wrong reading agrees with it
 * perfectly.
 *
 * ## The rule
 *
 * Walk the headings in FILE ORDER and accept one only when its number
 * continues the run. A quoted replacement article cannot continue the run,
 * because the amending instrument's own numbering is still going.
 *
 * Measured over every source the corpus had already mapped when this was
 * written: identical to {@link articlesInSource} on all 41, 0 differ. It does
 * not change a single one of the 1,279 articles already shipped, and
 * `article-parser.test.ts` holds it to that over whatever the corpus becomes.
 *
 * ## What it must NOT be used for on its own
 *
 * A run that stops early looks the same whether the instrument quotes another
 * one or the OCR simply lost a heading. Decree 284/2026 is not an amending
 * instrument; its own run is 1–23, and the scan lost Điều 15's heading at a
 * page break — the body is there, the heading is not — so this function
 * returns a run of 14 and rejects 16 through 23. **Fourteen is a plausible
 * article count for a decree, which is exactly what makes it dangerous.**
 *
 * So `rejected` is part of the return rather than swallowed: a caller that
 * finds it non-empty has a question it may not answer by itself. The generator
 * refuses to write a map in that state unless the instrument carries a
 * declaration saying which of the two readings applies.
 *
 * @param text page-delimited source text
 */
export function ownArticlesInSource(text, options) {
    if (typeof text !== 'string' || text.length === 0)
        return { run: [], rejected: [], highest: 0 };
    const re = headingPattern();
    const inFileOrder = [];
    let m;
    while ((m = re.exec(text)) !== null)
        inFileOrder.push(Number(m[1] ?? m[2]));
    // Sprint 62.42.0 — a THIRD cause of a run that stops early, beside the two
    // the docblock above names: the promulgated text omits the separator after
    // the number. Circular 34/2026/TT-BCT prints `Điều 13 Trách nhiệm của Cục
    // Đổi mới sáng tạo …` where all seventeen of its siblings print `Điều N.`,
    // so the reader stops at 12 for an instrument that has 18. That is not an
    // extraction artefact: the gazette's own .docx original reads the same.
    //
    // Widening `headingPattern` is not the fix and would be actively wrong. This
    // very file contains `Điều 22 Nghị định số 37/2026/NĐ-CP` and `Điều 15 Nghị
    // định số 37/2026/NĐ-CP` as CROSS-REFERENCES, and a pattern that accepts a
    // separator-less number accepts those too.
    //
    // So the repair is opt-in, per instrument, and constrained so that it can
    // only ever CLOSE a break and never invent an article: a separator-less
    // heading is admitted solely when its number is the one the run is already
    // waiting for. A cross-reference to Điều 22 cannot be admitted while the run
    // wants 13, and a declaration naming a number the run never reaches does
    // nothing. The stored heading is still held to the source in both directions
    // by `framework-articles-match-source.test.ts`, so a wrong line admitted here
    // fails there.
    const unseparated = new Set(options?.unseparated ?? []);
    const loose = unseparated.size === 0 ? null : /^[ \t_:.|—,'"“”¬lỈ-]*Đi[êề]u\s+(\d{1,3})\s+(?=\p{Lu})/gmu;
    const looseAt = new Map();
    if (loose !== null) {
        let lm;
        while ((lm = loose.exec(text)) !== null) {
            const n = Number(lm[1]);
            if (unseparated.has(n))
                looseAt.set(n, true);
        }
    }
    const run = [];
    let want = 1;
    for (const n of inFileOrder) {
        if (n !== want)
            continue;
        run.push(n);
        want += 1;
        // Close a declared break before reading on, so the numbers after it can
        // continue the run rather than being rejected behind it.
        while (looseAt.has(want)) {
            run.push(want);
            looseAt.delete(want);
            want += 1;
        }
    }
    const inRun = new Set(run);
    const rejected = [];
    for (const n of inFileOrder) {
        if (inRun.has(n) || rejected.includes(n))
            continue;
        rejected.push(n);
    }
    return { run, rejected, highest: inFileOrder.length === 0 ? 0 : Math.max(...inFileOrder) };
}
/**
 * Sprint 62.44.0 — the instrument numbers a string cites, as written.
 *
 * A requirement's `reference` routinely names more than one instrument:
 * `"Law 91/2025/QH15 Art. 21; Decree 356/2025/NĐ-CP Art. 19"` sits on a Law 91
 * requirement and on a Decree 356 one. {@link articlesInReference} needs to
 * know which article number belongs to which instrument, and this is how it
 * finds the instruments.
 *
 * Every limb is a SHAPE, never a number. R-46 forbids an instrument literal in
 * application code, and a parser that listed the instruments it knew would be
 * one more place for the corpus and the code to disagree the day an instrument
 * is added.
 *
 *   - `91/2025/QH15`, `356/2025/NĐ-CP`, `53/2022/ND-CP`, `83/2025/TT-NHNN`,
 *     `70/2026/TT-BGDĐT` — the Vietnamese number/year/issuer form;
 *   - `1671/QĐ-TTg`, `778/QĐ-BCA-A05` — a Vietnamese decision, which carries
 *     no year in its number;
 *   - `(EU) 2016/679`, `(EU) No 910/2014` — an EU regulation or directive
 *     number after its `(EU)` marker;
 *   - `2002/58/EC` — the older EU directive form.
 *
 * The negative lookbehind keeps a date out: `24/12/2024` would otherwise read
 * as `12/2024/…`, and the lookbehind refuses a number that starts right after
 * a slash or a digit.
 *
 * What it deliberately does NOT read: a numberless name (`GDPR`, `the Data
 * Law`, `Decree 13`). Measured over every mapped requirement on 2026-09-25,
 * the only numberless name that precedes an article number is `Decree 13` on
 * Decree 13's own record, which is exactly the "no citation, so the record's
 * own instrument" case below. Recognising names would need a list of names,
 * and a list is what R-46 forbids.
 */
function instrumentCitationPattern() {
    return /\((?:EU|EC|EEC|Euratom)\)\s*(?:No\.?\s*)?\d{1,4}\/\d{1,4}(?!\d)|(?<![\d/])\d{4}\/\d{1,4}\/(?:EC|EU|EEC)(?![A-Za-z])|(?<![\d/])\d{1,4}\/\d{4}\/[A-Za-zĐđ0-9]+(?:-[A-Za-zĐđ0-9]+)*|(?<![\d/])\d{1,5}\/[A-ZĐ]{1,4}-[A-Za-zĐđ0-9]+(?:-[A-Za-zĐđ0-9]+)*/gu;
}
/**
 * The comparison key of an instrument number: case, whitespace, an EU `No`
 * and the `Đ`/`D` spelling do not distinguish two instruments.
 *
 * A leading zero on the instrument's own number is dropped too (`09/2020/…`
 * and `9/2020/…` are one circular).
 *
 * The corpus writes both `53/2022/ND-CP` and `53/2022/NĐ-CP` for the same
 * decree — `instrument_number` fields were authored in ASCII while references
 * carry the diacritic — so an exact comparison would drop every article of
 * `VN_EID_DECREE_69_2024`, whose record says `69/2024/ND-CP` and whose
 * requirements say `69/2024/NĐ-CP`.
 */
export function instrumentNumberKey(citation) {
    return (String(citation ?? '')
        .normalize('NFC')
        .toUpperCase()
        .replace(/Đ/gu, 'D')
        .replace(/^(\((?:EU|EC|EEC|EURATOM)\))\s*NO\.?/u, '$1')
        .replace(/\s+/gu, '')
        // Gate E12.5 P2-2 — a leading zero on the LEADING number segment does not
        // distinguish two instruments either. Vietnamese citations print both
        // `09/2020/TT-BTTTT` and `9/2020/TT-BTTTT` for the same circular, and a
        // key that kept the zero would attribute nothing to a record authored in
        // one form and cited in the other. Only the leading segment, and never
        // its last digit: `0/…` stays `0/…`, and `10/…` is not `1/…`.
        .replace(/^(\((?:EU|EC|EEC|EURATOM)\))?0+(?=\d)/u, '$1'));
}
/**
 * Every instrument citation in a string, in order, with where it starts.
 *
 * Exported for `article-coverage-census.test.ts`, whose guard against an
 * article written BEFORE its instrument must recognise exactly the citations
 * the ownership rule does — a second pattern there would be one more place for
 * the two to disagree. It is deliberately not re-exported from the package
 * index: nothing in application code needs it.
 */
export function instrumentCitationsIn(text) {
    const out = [];
    if (typeof text !== 'string' || text.length === 0)
        return out;
    const re = instrumentCitationPattern();
    let m;
    while ((m = re.exec(text)) !== null)
        out.push({ index: m.index, citation: m[0] });
    return out;
}
/** Every string a translatable field carries: the string itself, or each locale's value. */
function stringsOf(value) {
    if (typeof value === 'string')
        return [value];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return Object.values(value).filter((v) => typeof v === 'string');
    }
    return [];
}
/**
 * Sprint 62.44.0 — the instrument number(s) a framework record is ABOUT.
 *
 * The declared number wins: `official_number` or `instrument_number`, when
 * either is authored, is the whole answer. Only a record that declares neither
 * falls back to its name, and then only to the FIRST number each name string
 * cites — because a name routinely cites a second instrument too, and that one
 * is never the record's own: `"Decree 320/2026/NĐ-CP amending Decree
 * 69/2024/NĐ-CP …"`.
 *
 * A number the record itself declares to be ANOTHER instrument's —
 * `amends_instrument`, or an entry of `replaces_unpackaged_instruments` — is
 * never adopted from a name either. The draft anti-spam decree has no number
 * of its own, and the first number its name cites is the decree it would
 * replace; adopting it would credit that decree's articles to the draft.
 *
 * Both the English and the Vietnamese name are read. `VN_AML_DECREE_19_2023` is
 * the reason: its English name says `Decree 19/2023`, which is not a citation,
 * and its Vietnamese name says `Nghị định 19/2023/NĐ-CP`, which is.
 *
 * An EMPTY answer means the record does not say which instrument it is. That
 * is harmless only while its references cite no instrument number either (a
 * draft with no number, whose references read `Draft Article 3`); otherwise
 * {@link articlesInReference} cannot attribute a cited article to it, and
 * `article-coverage-census.test.ts` fails on that record by name.
 *
 * @param options `citableOnly` keeps only the numbers the citation recogniser
 *   reads — see the note in the body. The census CREDITS through the full
 *   identity and never passes it there; its "no record loses a cited article
 *   for want of an identity" check does pass it (Gate E12.5 pass 3, P2-5), so
 *   that check and the backend reader share one definition of a usable identity
 * @returns the numbers as written, de-duplicated by {@link instrumentNumberKey}
 */
export function ownInstrumentNumbers(value, options) {
    // Gate E12.5 pass 2 (P0-1) — `citableOnly` answers a different question:
    // the part of the identity that can actually ATTRIBUTE an article, without
    // the declared numbers kept "as written" below.
    //
    // An identity can be non-empty and still attribute nothing. A record
    // authored with a short-form `official_number` such as `"NQ22/2026"` and no
    // `instrument_number` has the identity `["NQ22/2026"]`, and no reference can
    // cite that string in a shape the recogniser reads, so the strict ownership
    // rule credits the record with only the numbers cited before any
    // instrument. The census's crediting wants exactly that — loud — and never
    // passes the option; its identity check passes it, so an unreadable number
    // cannot excuse a record from that check. A reader of an INSTALLED record
    // that may predate its pack's `instrument_number` needs to tell "known"
    // from "unusable", and an EMPTY
    // `citableOnly` answer is how: the identity is absent or unreadable, and
    // either way it cannot attribute.
    //
    // An option rather than a second export so the package surface stays the
    // one function every caller already reads identity through.
    const entries = identityEntries(value);
    return (options?.citableOnly ? entries.filter((e) => e.citable) : entries).map((e) => e.number);
}
function identityEntries(value) {
    // `unknown`, not `InstrumentIdentityFields`: every caller holds a wider
    // record (a pack's `frameworks.json` row, an installed Mongo document), and
    // an all-optional parameter type is a TypeScript "weak type" that refuses
    // any argument sharing none of its keys at compile time — including the
    // record that declares no identity at all, which is exactly the case this
    // function must answer (with `[]`).
    if (!value || typeof value !== 'object')
        return [];
    const record = value;
    const out = new Map();
    const add = (citation, citable = true) => {
        const trimmed = citation.trim();
        if (trimmed.length === 0)
            return;
        const key = instrumentNumberKey(trimmed);
        if (!out.has(key))
            out.set(key, { number: trimmed, citable });
    };
    for (const field of [record.official_number, record.instrument_number]) {
        for (const value of stringsOf(field)) {
            const found = instrumentCitationsIn(value);
            // A declared number the pattern cannot read is kept as written rather
            // than dropped. It will then match nothing in a reference, and the
            // census reports every article the record cites as unattributed —
            // loud, which is the direction a shape this parser has never seen
            // should fail in. It is marked NOT citable, which is what
            // `ownInstrumentNumbers(record, { citableOnly: true })` leaves out.
            if (found.length === 0)
                add(value, false);
            else
                for (const f of found)
                    add(f.citation);
        }
    }
    if (out.size > 0)
        return [...out.values()];
    const foreign = new Set();
    for (const value of stringsOf(record.amends_instrument)) {
        for (const f of instrumentCitationsIn(value))
            foreign.add(instrumentNumberKey(f.citation));
    }
    const replaced = Array.isArray(record.replaces_unpackaged_instruments)
        ? record.replaces_unpackaged_instruments
        : [];
    for (const entry of replaced) {
        const number = entry?.instrument_number;
        for (const value of stringsOf(number)) {
            for (const f of instrumentCitationsIn(value))
                foreign.add(instrumentNumberKey(f.citation));
        }
    }
    for (const field of [record.name, record.display_name]) {
        for (const value of stringsOf(field)) {
            const first = instrumentCitationsIn(value)[0];
            if (first && !foreign.has(instrumentNumberKey(first.citation)))
                add(first.citation);
        }
    }
    return [...out.values()];
}
/**
 * Every article number a requirement `reference` names.
 *
 * Handles, because the corpus contains all of them:
 *
 *   "Law 91/2025/QH15 Art. 27"               -> [27]
 *   "Luật 91/2025/QH15 Điều 27"              -> [27]
 *   "Law 134/2025/QH15 Article 9"            -> [9]
 *   "Law 134/2025/QH15 Articles 1–7"         -> [1,2,3,4,5,6,7]
 *   "Articles 10, 13–15"                     -> [10,13,14,15]
 *   "Decree 356/2025/NĐ-CP Điều 8(2)(a)"     -> [8]
 *   "Law 60/2024/QH15"                       -> []   (no provision at all)
 *   "Law 116/2025/QH15 Arts. 3(3)-(5), 27, 29" -> [3,27,29]
 *   "Law 91/2025/QH15 Art. 37(1)(c), 37(2)(c)" -> [37]
 *
 * The `Law 60/2024/QH15` case is not a parse failure. It is the finding: a
 * requirement traceable to an instrument and to no article within it is one
 * nobody can verify, and the empty array is how the coverage reader sees that.
 *
 * ## `ownNumbers` — whose article is it?
 *
 * **Sprint 62.44.0.** Without `ownNumbers` this returns every article number
 * in the string, whichever instrument it belongs to, and that is exactly how
 * the coverage census and the Articles tab over-credited: `"Law 134/2025/QH15
 * — Articles 33–34"` on a Law 71 requirement marked Law 71's Điều 33 and 34
 * as implemented, and `"Law 91/2025/QH15 Art. 21; Decree 356/2025/NĐ-CP Art.
 * 19"` on a Decree 356 requirement marked Decree 356's Điều 21 — an article
 * number that belongs to the Law.
 *
 * With `ownNumbers` (see {@link ownInstrumentNumbers}), an article number
 * belongs to the instrument cited most recently BEFORE it in the string, and
 * only the numbers belonging to one of `ownNumbers` — or cited before any
 * instrument at all, which is the record's own by construction (`"Art. 5"` on
 * a GDPR requirement, `"Decree 13 Art. 24"` on Decree 13's) — are returned:
 *
 *   ("Law 91/2025/QH15 Art. 21; Decree 356/2025/NĐ-CP Art. 19", ["356/2025/NĐ-CP"]) -> [19]
 *   ("Law 116/2025/QH15 Art. 25(3)-(4); Decree 53/2022/ND-CP Arts. 26–27", ["116/2025/QH15"]) -> [25]
 *
 * An EMPTY `ownNumbers` is honoured literally — only uncited numbers are
 * returned — rather than read as "no filter". A record whose identity is
 * unknown cannot have a cited article attributed to it, and pretending
 * otherwise is the over-count this parameter exists to end.
 *
 * Known limit, recorded rather than guessed around: an article written BEFORE
 * its instrument (`"Điều 5 Nghị định 13/2023/NĐ-CP"`) belongs to whatever
 * was cited before it. No requirement is written that way (measured
 * 2026-09-26 over all 2,035), and `article-coverage-census.test.ts` fails the
 * day one is: it refuses any reference in which an article run is followed,
 * in the same `;`-separated segment, by an instrument citation — whatever
 * words stand between them (Gate E12.5 P1-7) — or by an instrument named
 * without a number (`Luật Bảo vệ …`, `Bộ luật Dân sự`, `AI Act`, `NĐ 13`),
 * and any in which an instrument this parser cannot read stands between an
 * article and the citation it would be credited to (`…; Decree 13 Art. 5`)
 * (pass 3, P2-3; pass 4, P2-5). That guard does NOT see an instrument named
 * only by an acronym (`Article 21 of the PDPL`) or in lower case: text cannot
 * tell `PDPL` from `DPIA` without a list of names, which R-46 forbids (pass 4,
 * P2-4). Such a reference credits the article to the instrument before it.
 *
 * @param reference the requirement's `reference` string
 * @param ownNumbers the requirement's own instrument number(s); omit for every
 *   article number in the string, which is the pre-62.44.0 behaviour
 * @returns ascending, de-duplicated
 */
export function articlesInReference(reference, ownNumbers) {
    // Gate E12.5 P1-6 — the integers are read off the SAME tokenisation as the
    // identities, so a lettered id holds its place in the run: `Art. 54, 54a and
    // 55` is 54 and 55 here (54a reads as its number) rather than stopping at the
    // letter and losing 55.
    const found = new Set();
    for (const id of articleIdentitiesIn(reference, ownNumbers))
        found.add(id.number);
    return [...found].sort((a, b) => a - b);
}
/**
 * The Vietnamese ordinal letters in alphabetical order. `đ` follows `d`, which
 * a code-point comparison gets wrong (`đ` sorts after `e`), and an empty suffix
 * — the bare article — precedes every letter: `6` comes before `6a`.
 */
const SUFFIX_ORDER = ['', 'a', 'b', 'c', 'd', 'đ', 'e'];
/**
 * The widest span a range — or a chain of ranges, first endpoint to last — may
 * enumerate. Beyond it the reference is a typo, and only its endpoints count.
 */
const MAX_RANGE_SPAN = 200;
function compareCited(a, b) {
    return a.number === b.number
        ? SUFFIX_ORDER.indexOf(a.suffix) - SUFFIX_ORDER.indexOf(b.suffix)
        : a.number - b.number;
}
/**
 * The shared tokeniser behind {@link articlesInReference} and
 * {@link articleIdsInReference}: every article identity a reference names,
 * with ranges expanded, in the order found (not de-duplicated).
 *
 * ## One grammar, one pass — Gate E12.5 P1-6
 *
 * An earlier version read the lettered ids with one grammar, then DELETED
 * them from the string — with the separator after them — and handed what was
 * left to the integer grammar. Deleting a token moves the boundaries around
 * it: `Art. 54, 54a and 55` became `54, and 55` and lost Điều 55, and `Art.
 * 10–12a, 15` became `10–15` and INVENTED Điều 13 and 14. So an item is now a
 * number with an optional ordinal letter, read in the same run as every other
 * item, and no boundary is ever rewritten.
 *
 * ## Ranges span integers, and a lettered endpoint is itself
 *
 * A range never reaches INTO an inserted article: `Arts. 6–9` is 6, 7, 8, 9
 * and not 6a. An inserted article is a distinct provision, and a drafter who
 * means it cites it. When an ENDPOINT is lettered, the endpoint is cited by
 * name, so it is included, and the integers are those that sit between the
 * two endpoints in the instrument's order (`n` < `na` < `n+1`):
 *
 *   `10–12a` -> 10, 11, 12, 12a   (12 precedes 12a, so it is inside the range)
 *   `12a–15` -> 12a, 13, 14, 15   (12 precedes 12a, so it is not)
 *   `6a–6c`  -> 6a, 6c            (no integer lies between; 6b is not reached)
 *   `5a–5b–5c` -> 5a, 5b, 5c      (a chain expands pair by pair, every endpoint kept)
 *   `1 to 5`, `5 through 7`, `Điều 5 đến Điều 7`
 *                                 (word connectors are ranges too — see below)
 *
 * A descending or absurd range is a typo in the reference, not a licence to
 * enumerate 900 articles, and yields its endpoints only. The test reads the
 * whole CHAIN (Gate E12.5 pass 3, P2-1): if any link descends, or the span
 * from the first endpoint to the last exceeds 200, every endpoint is kept and
 * nothing between them — so `1-200-400` is 1, 200 and 400, never 400 articles.
 *
 * ## A word range expands unless its far endpoint is a quantity (Gate E12.5 pass 5)
 *
 * `to`, `through` and `đến` — optionally with the article keyword repeated
 * before the far endpoint (`Điều 5 đến Điều 7`, `Article 5 to Article 7`) —
 * form a range exactly as a dash does, with ONE exception: when the far
 * endpoint is followed by a UNIT it is a quantity, never an article, and it
 * is dropped. Whatever else follows the endpoint does not matter, because if
 * the endpoint is an article the text is a range, and if it is not, it is no
 * article at all — there is no reading under which `Art. 5 to 7 inclusive` is
 * 5 and 7 without 6 (P2-8):
 *
 *   `Art. 5 to 7`, `Art. 5 to 7 inclusive`, `Art. 5 to 7 thereof`,
 *   `Articles 5 to 7 GDPR`, `Art. 5 to 7 (as amended)`   -> 5, 6, 7
 *   `Art. 5 to 7.2`, `Art. 5 to 7(2)`, `Điều 5 đến Điều 7 khoản 1`
 *                                  -> 5, 6, 7  (a clause of 7 still names 7)
 *   `Art. 5 to 7 through 9`        -> 5 to 9    (one chain)
 *   `Art. 12 to 30 days`, `Điều 12 đến 30 (ba mươi) ngày`
 *                                  -> 12        (a unit: 30 is a quantity)
 *   `Art. 5 to the extent …`       -> 5         (a tail must be a number)
 *
 * The units are `days` (optionally after `working`, `business` or
 * `calendar`), `hours`, `weeks`, `months`, `years` and `minutes`, each
 * singular or plural; `percent`, `per cent` (pass 6, P2-4) and `%`; and
 * `ngày` (so `ngày làm việc`), `giờ`, `tuần`, `tháng`, `năm` and `phút` —
 * every word unit as a whole word, in any case. They are recognised after the
 * endpoint's clauses, a decimal (`30.5 days`), and any parenthesised gloss of
 * up to 40 characters (`30 (thirty) days`, pass 5 P2-6). A unit this list does
 * not know reads as an article range — the direction a shape the parser has
 * not been taught fails in is visible in the Articles tab.
 *
 * Every endpoint of an item is one chain, dash and word links alike, so the
 * cap and the descending test above read the WHOLE item (pass 5, P2-5):
 * `Art. 1 to 200 to 400` is 1, 200 and 400, as `1-200-400` is.
 *
 * History, because each step was a finding: pass 3 (P2-2) required a word
 * range to end at a boundary and dropped the far endpoint otherwise, which
 * lost cited articles (`Articles 44 to 49 GDPR` read 44 alone); pass 4 (P2-6)
 * widened the boundary and kept a failing endpoint as itself, which gave the
 * `[5, 7]` reading and split chains out from under the cap.
 */
function articleIdentitiesIn(reference, ownNumbers) {
    if (typeof reference !== 'string' || reference.length === 0)
        return [];
    const out = [];
    const ownKeys = ownNumbers ? new Set(ownNumbers.map(instrumentNumberKey)) : null;
    const citations = ownKeys ? instrumentCitationsIn(reference) : [];
    // `Điều`/`Article`/`Articles`/`Art.`/`Arts.` then a run of article items.
    //
    // **Sprint 62.34.2 — `and` and `và` were missing, and it cost articles.**
    // The run stopped at the first letter, so `"Draft Articles 1, 2 and 3"`
    // returned [1, 2]. Measured across the corpus before the fix: **34
    // references in 7 frameworks** each lost the number after the conjunction,
    // eighteen of them in the draft anti-spam pack, where `VN-ASR-R01` had cited
    // Điều 3 since Sprint 62.28.0 and the coverage census had never seen it.
    //
    // **Sprint 62.44.0 — the run also stopped at a clause suffix, and it cost
    // articles for the same reason.** `"Arts. 3(3)-(5), 27, 29"` returned [3]:
    // the `(` ended the run, and Điều 27 and 29 of the Cybersecurity Law read as
    // unimplemented while a requirement cited both. An item is now an article
    // identity, any number of `(clause)`/`(point)` suffixes, and an optional
    // range tail — to another identity (`1–7`, `5(1)–7(2)`, `10–12a`) or to
    // another suffix of the same article (`25(3)-(4)`, which is ONE article). The
    // suffixes are consumed and never counted: `8(2)(a)` is Điều 8, not 8, 2 and 1.
    //
    // The ordinal letter must END the token (`(?![\p{L}\d])`), so `6a` is an
    // identity and a number running into a word is not given a letter. The
    // class is the letters actually used for inserted articles — see
    // {@link articleIdsInSource} for why it is not `[a-z]`.
    //
    // The conjunctions are matched with surrounding whitespace rather than with
    // `\b`, because `\b` is ASCII-based even under `/u` and `à` is not a `\w`
    // character — `\bvà\b` matches nothing. The keyword keeps a letter
    // lookbehind for the same reason `\b` cannot be used: `Parts 3` must not read
    // as `Arts 3`.
    const clause = String.raw `(?:\s*\([0-9a-zđ]{1,4}\))*`;
    // Gate E12.5 pass 2 (P2-4) — a clause listed on its own after a separator
    // (`Art. 5(1), (2) and 6`) belongs to the article before it. It is a legal
    // item of the run so that the run reaches `6`, and it names no article.
    const clauseOnly = String.raw `\([0-9a-zđ]{1,4}\)${clause}`;
    const idNoCapture = String.raw `\d{1,3}(?!\d)(?:[a-eđ](?![\p{L}\d]))?`;
    const keyword = String.raw `(?<!\p{L})(?:Điều|Articles?|Arts?\.?)`;
    // A range connector is a dash, or a word — `to`, `through`, `đến` — between
    // whitespace. A tail must name something — an identity or a clause — so
    // `Art. 5 to the extent` and `Art. 5 to 2030` are Article 5 alone (a
    // four-digit number is not an identity, by `(?!\d)`).
    //
    // `đến` is a connector under the same rule as `to`, because the two
    // languages must read one shape one way. A word connector's endpoint may
    // repeat the article keyword, which is how Vietnamese writes a range and how
    // English sometimes does (`Article 5 to Article 7`). A dash's endpoint may
    // not: no reference writes `Điều 5 – Điều 7`, and it would widen the dash
    // for nothing.
    //
    // The one condition on a WORD connector is the unit lookahead below: `Art.
    // 12 to 30 days` is a time limit, not an article, so its `30` is neither a
    // range end nor an identity (pass 3, P2-2). Nothing else about the text
    // after the endpoint matters (pass 5, P2-8; see the docblock above). A dash
    // sits between two numbers and nothing else, so it carries no condition.
    // The unit may follow the endpoint's clauses, which is why they are skipped
    // inside the lookahead — a backtrack that gives a clause back cannot slip
    // the tail past it.
    //
    // Gate E12.5 pass 5 (P2-6, P2-7) — the groups skipped are ANY parenthesised
    // text up to 40 characters, not only a clause: `Điều 12 đến 30 (ba mươi)
    // ngày` spells the quantity out in words, and a clause-shaped skip let the
    // gloss hide the unit and minted Điều 12 to 30. A decimal is skipped too
    // (`30.5 days`). An optional ordinal letter is read first so a backtrack
    // that gives the letter back cannot slip the tail past the unit either. The
    // units are those a time limit or a share is stated in: `hours` and `weeks`
    // were missing, and `business`/`calendar` join `working` before `days`.
    const unitWord = String.raw `(?:(?:working|business|calendar)\s+)?days?|hours?|weeks?|months?|years?|minutes?|percent|per\s+cent|` +
        String.raw `ngày|giờ|tuần|tháng|năm|phút`;
    const unit = String.raw `(?![a-eđ]?(?:\.\d+)?(?:\s*\([^()]{1,40}\))*\s*(?:%|(?:${unitWord})(?!\p{L})))`;
    const rangeTail = String.raw `(?:\s*[–—-]\s*(?:${idNoCapture}${clause}|${clauseOnly})` +
        String.raw `|\s+(?:to|through|đến)\s+(?:${keyword}\s*)?(?:${idNoCapture}${clause}|${clauseOnly})${unit})`;
    // Any number of range tails, so a CHAIN keeps every endpoint: `5a-5b-5c`
    // was read as `5a-5b` by the first one-pass grammar, which allowed one tail
    // (pass 2, P2-4 — the one regression that rewrite introduced).
    const itemNoCapture = String.raw `${idNoCapture}${clause}(?:${rangeTail})*`;
    // `, and 7` — the Oxford comma — is one separator, not a comma followed by a
    // word that ends the run.
    const separator = String.raw `(?:\s*,\s*(?:(?:and|và)\s+)?|\s+(?:and|và)\s+)`;
    const run = String.raw `${itemNoCapture}(?:${separator}(?:${itemNoCapture}|${clauseOnly}))*`;
    const re = new RegExp(String.raw `(?<!\p{L})(?:Điều|Articles?|Arts?\.?)\s*(${run})`, 'giu');
    // The items of a matched run. The lookbehind keeps the scan out of a clause
    // (`(2)`, `(12)`, `(a1)`): an item never starts right after `(`, a digit or
    // a letter, so a clause's digits are never read as an article.
    const itemRe = new RegExp(String.raw `(?<![\d(\p{L}])${itemNoCapture}`, 'giu');
    // The endpoints of one item, once its clauses are removed. `5(1)-(3)-7` is
    // `5--7`: endpoints 5 and 7, the clause span inside it naming no article.
    const endpointRe = new RegExp(String.raw `(\d{1,3})(?!\d)([a-eđ](?![\p{L}\d]))?`, 'giu');
    const clauseRe = /\([0-9a-zđ]{1,4}\)/giu;
    // Blanked to spaces rather than deleted, so the digits either side of a
    // clause can never run together into one number.
    const blank = (s) => ' '.repeat(s.length);
    let m;
    while ((m = re.exec(reference)) !== null) {
        if (ownKeys) {
            let owner = null;
            for (const c of citations) {
                if (c.index >= m.index)
                    break;
                owner = c.citation;
            }
            if (owner !== null && !ownKeys.has(instrumentNumberKey(owner)))
                continue;
        }
        itemRe.lastIndex = 0;
        let it;
        while ((it = itemRe.exec(m[1])) !== null) {
            const endpoints = [];
            endpointRe.lastIndex = 0;
            let e;
            const bare = it[0].replace(clauseRe, blank);
            while ((e = endpointRe.exec(bare)) !== null) {
                endpoints.push({ number: Number(e[1]), suffix: (e[2] ?? '').toLowerCase() });
            }
            if (endpoints.length === 0)
                continue;
            // Every endpoint of an item is one CHAIN, dash or word alike (Gate E12.5
            // pass 5, P2-5 and P2-8). Pass 4 split the item at a word connector
            // whose far endpoint was followed by prose and kept that endpoint on its
            // own, which read `Art. 5 to 7 inclusive` as [5, 7] — wrong whether 7 is
            // an article or a quantity — and let each piece take its own cap, so
            // `Art. 1 to 200 to 400` minted 202 identities. A tail that is a
            // quantity never becomes an endpoint (the `unit` lookahead refuses it),
            // so there is nothing left to split, and the cap and the descending test
            // read the whole item at once.
            out.push(...expandChain(endpoints));
        }
    }
    return out;
}
/**
 * Every identity one chain of linked endpoints names: `5-7-9` is 5 to 9.
 *
 * Gate E12.5 pass 3 (P2-1) — the cap reads the WHOLE chain, from its first
 * endpoint to its last. Pass 2 capped each link, so `Art. 1-200-400` minted
 * 400 articles and every further 200-wide link minted 200 more, where `Art.
 * 1-400` yields two. A chain with any descending link, or spanning more than
 * 200, is a typo: every endpoint it names is kept, and nothing between them is
 * invented.
 */
function expandChain(endpoints) {
    const first = { number: endpoints[0].number, suffix: endpoints[0].suffix };
    const out = [first];
    const last = endpoints[endpoints.length - 1];
    const absurd = last.number - first.number > MAX_RANGE_SPAN ||
        endpoints.some((hi, i) => i > 0 && compareCited(hi, endpoints[i - 1]) < 0);
    if (absurd) {
        for (const e of endpoints.slice(1))
            out.push({ number: e.number, suffix: e.suffix });
        return out;
    }
    // A chain expands pair by pair: `5-7-9` is 5–7 then 7–9. Each pair's rule
    // is the one below, so a chain can never reach past its last endpoint or
    // into a lettered article it does not name.
    for (let i = 1; i < endpoints.length; i += 1) {
        const lo = endpoints[i - 1];
        const hi = endpoints[i];
        for (let n = lo.suffix ? lo.number + 1 : lo.number; n <= hi.number; n += 1) {
            out.push({ number: n, suffix: '' });
        }
        if (hi.suffix)
            out.push({ number: hi.number, suffix: hi.suffix });
    }
    return out;
}
/**
 * The join key for an article identity, which is NOT its number.
 *
 * Sprint 62.42.0 — a Vietnamese amending law INSERTS `Điều 6a` between `Điều 6`
 * and `Điều 7` rather than renumbering, so that every cross-reference in every
 * other instrument keeps pointing at the same provision. Ten such articles sit
 * in the consolidated Product and Goods Quality Law alone.
 *
 * `6` and `6a` are different articles and must not collide. Everything that
 * joins a requirement reference to an article — the coverage census, the
 * match-source gate, the backend reader — keys on this rather than on `number`.
 *
 * @param article the number, and the letter if the instrument appends one
 * @returns `"27"` for a plain article, `"6a"` for an inserted one
 */
export function articleKey(article) {
    return `${article.number}${article.suffix ?? ''}`;
}
/**
 * Every article IDENTITY a committed signed text declares, in order.
 *
 * The suffix-aware companion to {@link articlesInSource}, which returns
 * integers and therefore cannot distinguish `Điều 6` from `Điều 6a`. That
 * function is unchanged and still serves every instrument whose articles are
 * plain integers; this one is what a map of an amended instrument is built and
 * checked against.
 *
 * The suffix class is the Vietnamese ordinal letters actually used for this —
 * `a` to `e` plus `đ`, which follows `d` in the Vietnamese alphabet. It is not
 * `[a-z]`: a wider class would let `Điều 12b` in a citation style nobody uses
 * become an article, and the corpus contains no case needing it.
 *
 * Sprint 63.33.0 — an EU limb, the same line grammar as the second limb of
 * {@link articlesInSource} (`Article 12` alone on its line) with the same
 * letter class: the consolidated ePrivacy Directive inserts `Article 14a` and
 * `Article 15a` (Directive 2009/136/EC Art. 2 points 8 and 10). Before this the
 * function read no EU line at all; no committed EU source carries a lettered
 * article other than that one, so every other map reads exactly as before.
 *
 * @param text page-delimited source text
 * @returns keys in file order, de-duplicated, e.g. `["1", "6", "6a", "6b"]`
 */
export function articleIdsInSource(text) {
    if (typeof text !== 'string' || text.length === 0)
        return [];
    const re = /^[ \t_:.|—,'"“”¬lỈ-]*(?:Đi[êề]u\s+(\d{1,3})([a-eđ]?)\s*[.:—-]|Article\s+(\d{1,3})([a-e]?)\s*(?:[.:—-]|$))/gmu;
    const seen = new Set();
    const out = [];
    let m;
    while ((m = re.exec(text)) !== null) {
        const key = `${Number(m[1] ?? m[3])}${m[2] ?? m[4] ?? ''}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(key);
    }
    return out;
}
/**
 * The article IDENTITIES a committed signed text declares as ITS OWN, in order.
 *
 * {@link articleIdsInSource} reads every lettered heading line, whoever's it
 * is. An amending instrument that INSERTS an article into another quotes it:
 * Law 108/2025/QH15 Điều 51 adds `“Điều 70a. Cung cấp dịch vụ kế toán …` to
 * the Accounting Law 88/2015/QH13, and the flat identity reading returns
 * `70a` between `51` and `52` — a 54th article of a 53-article law. The
 * numeric own run cannot see it (its grammar never matches `70a.`), so it
 * rejects nothing and no `source_reading` can be declared for it.
 *
 * The rule (operator decision, Law 108 intake S3, 2026-10-27): a LETTERED
 * heading whose line opens with a quotation mark (`“` or `"`, inside the debris
 * class) is the instrument's own only when its number continues the
 * instrument's own numbering — that is, equals the number of the nearest
 * preceding heading that does NOT open with a quotation mark. `“Điều 70a.`
 * after `Điều 51.` is refused; `"Điều 6b.` after `Điều 6a.` is read, because
 * the debris class also absorbs a stray quote the OCR leaves before a heading
 * and an inserted article of the instrument's own always follows its plain
 * sibling. An unquoted heading, and every plain one, is read exactly as
 * {@link articleIdsInSource} reads it; plain quoted headings are the numeric
 * own run's to refuse ({@link ownArticlesInSource}).
 *
 * Measured over the 234 committed transcripts that carry a heading, on
 * 2026-10-27: it differs from {@link articleIdsInSource} on 11, every one an
 * instrument quoting the lettered articles it inserts into another. The seven
 * that are mapped all declare an `own_run` reading and so are read on numbers,
 * never on identities; the other four (Law 108 among them) had no map. On both
 * sets read by identity (the consolidated Product and Goods Quality Law, the
 * consolidated ePrivacy Directive) it is identical, so no committed map moves —
 * `article-parser.test.ts` holds that over whatever the corpus becomes.
 *
 * @param text page-delimited source text
 * @returns keys in file order, de-duplicated
 */
export function ownArticleIdsInSource(text) {
    if (typeof text !== 'string' || text.length === 0)
        return [];
    const re = /^([ \t_:.|—,'"“”¬lỈ-]*)(?:Đi[êề]u\s+(\d{1,3})([a-eđ]?)\s*[.:—-]|Article\s+(\d{1,3})([a-e]?)\s*(?:[.:—-]|$))/gmu;
    const seen = new Set();
    const out = [];
    let lastUnquoted = null;
    let m;
    while ((m = re.exec(text)) !== null) {
        const number = Number(m[2] ?? m[4]);
        const suffix = m[3] ?? m[5] ?? '';
        const quoted = /["“]/u.test(m[1]);
        if (!quoted)
            lastUnquoted = number;
        else if (suffix !== '' && number !== lastUnquoted)
            continue;
        const key = `${number}${suffix}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(key);
    }
    return out;
}
/**
 * Every article IDENTITY a requirement `reference` names.
 *
 * The suffix-aware companion to {@link articlesInReference}. That function
 * returns integers, so `"Art. 6a"` and `"Art. 6"` are the same answer to it —
 * which is correct for an instrument whose articles are plain integers and
 * wrong for one that has had articles inserted.
 *
 * Ranges stay integer-only and deliberately so. `"Arts. 6–9"` expands to
 * `6, 7, 8, 9` and does NOT reach into `6a`: an inserted article is a distinct
 * provision and a drafter who means it cites it, so silently pulling it into a
 * numeric span would attribute to a requirement an article nobody said it
 * covered. A lettered ENDPOINT is cited by name and is included:
 * `"Art. 10–12a"` is `10, 11, 12, 12a`, and `"Art. 12a–15"` is `12a, 13, 14,
 * 15` (see `articleIdentitiesIn` for the rule).
 *
 * `ownNumbers` applies the same ownership rule as {@link articlesInReference}
 * (Sprint 62.44.0): an article identity belongs to the instrument cited most
 * recently before it, and only the record's own are returned. Omitting it
 * keeps every identity in the string.
 *
 * @param reference the requirement's `reference` string
 * @param ownNumbers the requirement's own instrument number(s); see
 *   {@link ownInstrumentNumbers}
 * @returns keys in ascending order, de-duplicated
 */
export function articleIdsInReference(reference, ownNumbers) {
    // A single marker introduces a LIST: "Art. 6a, 6b, 23a" names three
    // articles, not one, and "Art. 54, 54a and 55" names three of which one is
    // lettered. Both halves — the lettered ids and the bare numbers — come from
    // the one tokenisation in `articleIdentitiesIn`, so neither can move the
    // other's boundaries (Gate E12.5 P1-6). A suffixed citation never also
    // registers as its bare number: "Art. 6a" names 6a and says nothing about 6.
    const seen = new Map();
    for (const cited of articleIdentitiesIn(reference, ownNumbers)) {
        const key = articleKey({ number: cited.number, suffix: cited.suffix || undefined });
        if (!seen.has(key))
            seen.set(key, cited);
    }
    return [...seen.entries()].sort(([, a], [, b]) => compareCited(a, b)).map(([key]) => key);
}
//# sourceMappingURL=article-parser.js.map